<template>
  <div
    v-loading="editor.loading.value"
    :class="standalone ? 'mp-editor-embedded' : 'mp-editor'"
  >
    <!-- 活动列表 → 修改：嵌入 ContentWrap，与详情/预览同一管理端风格 -->
    <template v-if="standalone">
      <div class="mp-editor-embedded__header">
        <el-button link type="primary" class="!px-0" @click="handleBack">
          <Icon icon="ep:arrow-left" :size="14" class="mr-4px" />
          返回列表
        </el-button>
        <span class="mp-editor-embedded__title">{{ editor.editorTitle.value }}</span>
        <div class="mp-editor-embedded__actions">
          <el-button :loading="editor.saving.value" @click="editor.saveDraft()">保存</el-button>
          <el-button :loading="editor.saving.value" @click="handleStandalonePreview">预览</el-button>
          <el-button type="primary" :loading="editor.saving.value" @click="handlePublish">
            发布活动
          </el-button>
        </div>
      </div>
      <el-steps
        :active="edStep"
        align-center
        finish-status="success"
        class="mp-editor-embedded__steps"
      >
        <el-step v-for="step in steps" :key="step" :title="step" />
      </el-steps>
    </template>

    <!-- 活动市场 → 立即使用：全屏门户向导 -->
    <div v-else class="mp-editor__top">
      <div class="mp-editor__back" @click="handleBack">
        <Icon icon="ep:arrow-left" :size="14" />
        返回
      </div>
      <div class="mp-editor__title">{{ editor.editorTitle.value }}</div>
      <div class="mp-editor__steps">
        <template v-for="(step, i) in steps" :key="step">
          <div class="mp-editor__step" :class="stepClass(i)" @click="goToStep(i)">
            <span class="mp-editor__num">
              <Icon v-if="i < edStep" icon="ep:check" :size="12" />
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="mp-editor__txt">{{ step }}</span>
          </div>
          <span v-if="i < steps.length - 1" class="mp-editor__line"></span>
        </template>
      </div>
      <div v-if="!readonly" class="mp-editor__actions">
        <button class="mp-btn-ghost" type="button" :disabled="editor.saving.value" @click="editor.saveDraft()">
          保存
        </button>
        <button class="mp-btn-ghost" type="button" :disabled="editor.saving.value" @click="editor.preview()">
          预览
        </button>
        <button class="mp-btn-primary" type="button" :disabled="editor.saving.value" @click="handlePublish">
          发布活动
        </button>
      </div>
      <div v-else-if="mode === 'preview'" class="mp-editor__actions">
        <button class="mp-btn-primary" type="button" @click="editor.preview()">复制预览链接</button>
      </div>
      <div v-else-if="mode === 'detail'" class="mp-editor__actions">
        <button class="mp-btn-primary" type="button" @click="switchToEdit">编辑活动</button>
      </div>
    </div>

    <div :class="standalone ? 'mp-editor-embedded__body' : 'mp-editor__body'">
      <div :class="standalone ? 'mp-editor-embedded__left' : 'mp-editor__left'">
        <!-- 步骤1 基础 + 奖品 -->
        <div v-show="edStep === 0" class="mp-ed-panel">
          <h3>基础设置</h3>
          <el-form ref="basicFormRef" :model="basicForm" :rules="basicRules" label-width="96px">
            <el-form-item label="活动名称" prop="actName">
              <el-input v-model="editor.actName.value" :disabled="readonly" maxlength="80" placeholder="请输入活动名称" />
              <div class="mp-fhint">名称将显示在活动页面标题与分享卡片</div>
            </el-form-item>
            <el-form-item label="活动时间" prop="timeRange">
              <el-date-picker
                v-model="timeRange"
                :disabled="readonly"
                :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
                class="!w-full"
                end-placeholder="结束时间"
                start-placeholder="开始时间"
                type="datetimerange"
                value-format="YYYY-MM-DD HH:mm:ss"
              />
            </el-form-item>
            <el-form-item label="活动规则" prop="actRules">
              <el-input v-model="editor.actRules.value" :disabled="readonly" :rows="4" type="textarea" placeholder="请输入活动规则" />
            </el-form-item>
            <el-form-item :label="`${playActionLabel(editor.playStyle.value)}次数`">
              <div class="mp-switch-row mp-switch-row--inline">
                <span>每人每天可{{ playActionLabel(editor.playStyle.value) }}</span>
                <el-input-number v-model="editor.dailyLimit.value" :disabled="readonly" :min="0" :max="999" controls-position="right" />
                <span>次</span>
              </div>
              <div v-if="editor.isLotteryStyle.value" class="mp-switch-row">
                <el-switch v-model="editor.shareExtra.value" :disabled="readonly" />
                <span>分享活动后可额外增加{{ playActionLabel(editor.playStyle.value) }}次数</span>
              </div>
              <div v-else-if="editor.playStyle.value === 'fission'" class="mp-fhint">
                裂变玩法请在「活动列表 → 编辑活动」中配置助力规则与奖励
              </div>
            </el-form-item>
          </el-form>
        </div>

        <div v-show="edStep === 0 && editor.isLotteryStyle.value" class="mp-ed-panel">
          <h3>奖品设置</h3>
          <el-table :data="editor.prizes.value" border class="mp-prize-el-table">
            <el-table-column label="奖品名称" min-width="140">
              <template #default="{ row }">
                <el-input v-model="row.name" :disabled="readonly" placeholder="奖品名称" />
              </template>
            </el-table-column>
            <el-table-column label="类型" width="120">
              <template #default="{ row }">
                <el-select v-model="row.type" :disabled="readonly" class="!w-full">
                  <el-option label="实物" value="实物" />
                  <el-option label="虚拟" value="虚拟" />
                  <el-option label="现金" value="现金" />
                  <el-option label="空" value="空" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="数量" width="110">
              <template #default="{ row }">
                <el-input-number v-model="row.count" :disabled="readonly" :min="0" class="!w-full" controls-position="right" />
              </template>
            </el-table-column>
            <el-table-column label="中奖概率(%)" width="130">
              <template #default="{ row }">
                <el-input-number
                  v-model="row.probability"
                  :disabled="readonly"
                  :min="0"
                  :max="100"
                  :precision="1"
                  :step="0.1"
                  class="!w-full"
                  controls-position="right"
                />
              </template>
            </el-table-column>
            <el-table-column v-if="!readonly" label="操作" width="72" align="center">
              <template #default="{ $index }">
                <el-button link type="danger" @click="removePrize($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button v-if="!readonly" class="mp-btn-add-el" type="primary" plain @click="addPrize">
            <Icon icon="ep:plus" :size="14" class="mr-4px" />
            添加奖品
          </el-button>
          <div class="mp-fhint">优惠券/积分奖品请保存后在「编辑活动」中配置后再发布</div>
        </div>

        <div v-show="edStep === 0 && !editor.isLotteryStyle.value" class="mp-ed-panel">
          <h3>玩法说明</h3>
          <p class="mp-fhint">
            当前模板玩法为「{{ editor.styleName.value }}」。保存后将创建
            <code>{{ editor.playStyle.value }}</code> 类型活动，具体玩法配置可在「活动列表 → 编辑活动」中完善。
          </p>
        </div>

        <!-- 步骤2 高级 -->
        <div v-show="edStep === 1" class="mp-ed-panel">
          <h3>高级设置</h3>
          <el-form label-width="96px">
            <el-form-item label="分享标题">
              <el-input v-model="editor.shareTitle.value" :disabled="readonly" placeholder="分享卡片标题" />
            </el-form-item>
            <el-form-item label="分享描述">
              <el-input v-model="editor.shareDesc.value" :disabled="readonly" placeholder="分享卡片描述" />
            </el-form-item>
            <el-form-item label="参与限制">
              <div class="mp-switch-row">
                <el-switch v-model="editor.needFollow.value" :disabled="readonly" />
                <span>参与活动需关注公众号</span>
              </div>
              <div class="mp-switch-row">
                <el-switch v-model="editor.phoneAuth.value" :disabled="readonly" />
                <span>手机号授权登录（获取用户手机号）</span>
              </div>
              <div class="mp-switch-row">
                <el-switch v-model="editor.antiCheat.value" :disabled="readonly" />
                <span>开启防作弊（同一设备/微信号限制）</span>
              </div>
            </el-form-item>
          </el-form>
        </div>

        <!-- 步骤3 样式 -->
        <div v-show="edStep === 2" class="mp-ed-panel">
          <h3>样式设置</h3>
          <div class="mp-preview-tip">
            <p>右侧预览会随下方配置<strong>实时更新</strong>。标题、时间、规则、封面、主题色均可在此步骤调整。</p>
            <p>玩法区按钮文案（如「立即邀请」）及助力规则，请保存后在「活动列表 → 编辑活动」中完善。</p>
          </div>
          <div class="mp-frow">
            <div class="mp-flabel">活动标题</div>
            <div class="mp-fctl">
              <el-input v-model="editor.actName.value" :disabled="readonly" maxlength="80" placeholder="显示在手机预览顶部" />
            </div>
          </div>
          <div class="mp-frow">
            <div class="mp-flabel">活动规则</div>
            <div class="mp-fctl">
              <el-input
                v-model="editor.actRules.value"
                :disabled="readonly"
                :rows="3"
                type="textarea"
                placeholder="显示在预览「活动规则」页签"
              />
            </div>
          </div>
          <div class="mp-frow">
            <div class="mp-flabel">主题配色</div>
            <div class="mp-fctl">
              <div class="mp-swatches">
                <div
                  v-for="c in editor.themeColors.value"
                  :key="c"
                  class="mp-swatch"
                  :class="{ 'is-active': editor.themeColor.value === c, 'is-disabled': readonly }"
                  :style="{ background: c }"
                  @click="!readonly && (editor.themeColor.value = c)"
                ></div>
              </div>
            </div>
          </div>
          <div class="mp-frow">
            <div class="mp-flabel">活动封面</div>
            <div class="mp-fctl">
              <div class="mp-bg-grid">
                <div
                  v-for="id in bgIds"
                  :key="id"
                  class="mp-bg-item"
                  :class="{ 'is-active': editor.selectedBgId.value === id, 'is-disabled': readonly }"
                  @click="!readonly && (editor.selectedBgId.value = id)"
                >
                  <img v-if="getCoverUrl(id)" :src="getCoverUrl(id)" alt="" />
                  <span class="mp-bg-ck"><Icon icon="ep:check" :size="10" /></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 步骤4 完成 -->
        <div v-show="edStep === 3" class="mp-ed-panel">
          <div class="mp-finish">
            <div class="mp-finish__ok">
              <Icon icon="ep:circle-check-filled" :size="36" />
            </div>
            <h2>活动创建完成！</h2>
            <p>
              {{
                editor.published.value
                  ? '活动已发布，用户现在即可参与'
                  : '活动已保存为草稿，发布后用户即可参与'
              }}
            </p>
            <div class="mp-finish__link">
              <span>{{ editor.activityLink.value || '保存后生成活动链接' }}</span>
              <button type="button" class="mp-finish__copy" @click="editor.copyLink()">
                <Icon icon="ep:document-copy" :size="14" class="mr-4px" />
                复制
              </button>
            </div>
            <div class="mp-finish__actions">
              <button type="button" class="mp-btn-ghost" @click="goActivityList">查看活动列表</button>
              <button
                v-if="editor.activityId.value"
                type="button"
                class="mp-btn-primary"
                @click="goActivityEdit"
              >
                编辑活动
              </button>
            </div>
          </div>
        </div>

        <div :class="standalone ? 'mp-editor-embedded__footer' : 'mp-step-btns'">
          <template v-if="standalone">
            <el-button v-if="edStep > 0" @click="edStep--">上一步</el-button>
            <el-button v-if="edStep < 3" type="primary" @click="nextStep">下一步</el-button>
            <el-button
              v-if="edStep === 3"
              type="success"
              :loading="editor.saving.value"
              @click="handlePublish"
            >
              直接发布
            </el-button>
          </template>
          <template v-else>
            <button v-if="edStep > 0" class="mp-btn-ghost" type="button" @click="edStep--">上一步</button>
            <button v-if="edStep < 3" class="mp-btn-primary" type="button" @click="nextStep">下一步</button>
            <button
              v-if="edStep === 3 && !readonly"
              class="mp-btn-success"
              type="button"
              :disabled="editor.saving.value"
              @click="handlePublish"
            >
              直接发布
            </button>
            <button
              v-if="edStep === 3 && mode === 'preview'"
              class="mp-btn-primary"
              type="button"
              @click="editor.preview()"
            >
              复制预览链接
            </button>
          </template>
        </div>
      </div>

      <div :class="standalone ? 'mp-editor-embedded__right' : 'mp-editor__right'">
        <div class="mp-editor__preview-label">iPhone 18 Pro 预览</div>
        <MarketPhonePreview
          :act-name="editor.actName.value"
          :banner-url="bannerUrl"
          :theme-color="editor.themeColor.value"
          :play-style="editor.playStyle.value"
          :start-time="editor.startTime.value"
          :end-time="editor.endTime.value"
          :rules-text="editor.actRules.value"
          :prizes="editor.prizes.value"
          :page-tabs="editor.pageTabs.value"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { FormInstance, FormRules } from 'element-plus'
import { computed, inject, reactive, ref, watch } from 'vue'
import { MARKET_PORTAL_KEY } from '../useMarketPortal'
import { useMarketPortalStore } from '../useMarketPortalStore'
import { useMarketActivityEditor, type MarketEditorMode } from '../useMarketActivityEditor'
import { usePromotionActivityNav } from '@/hooks/web/usePromotionActivityNav'
import { playActionLabel } from '../playStyles'
import MarketPhonePreview from './MarketPhonePreview.vue'

defineOptions({ name: 'MarketActivityEditor' })

const props = withDefaults(
  defineProps<{
    templateId?: number
    activityId?: number
    mode?: MarketEditorMode
    standalone?: boolean
  }>(),
  {
    mode: 'create',
    standalone: false
  }
)

const message = useMessage()
const portal = inject(MARKET_PORTAL_KEY, null)
const toastFn = (msg: string) => (portal ? portal.toast(msg) : message.info(msg))
const { getCoverUrl, festivalTabs } = useMarketPortalStore()
const editor = useMarketActivityEditor(toastFn, getCoverUrl)
const { goList, goPreview } = usePromotionActivityNav()

const readonly = computed(() => editor.readonly.value)
const standalone = computed(() => props.standalone)

const handleBack = () => {
  if (props.standalone || !portal) goList()
  else portal.goto('market')
}

const handleStandalonePreview = () => {
  if (editor.activityId.value) {
    goPreview(editor.activityId.value)
    return
  }
  editor.preview()
}

const steps = ['基础设置', '高级设置', '样式设置', '完成']
const edStep = ref(0)
const basicFormRef = ref<FormInstance>()
const timeRange = ref<string[]>([])

const basicForm = reactive({
  get actName() {
    return editor.actName.value
  },
  set actName(v: string) {
    editor.actName.value = v
  },
  get actRules() {
    return editor.actRules.value
  },
  set actRules(v: string) {
    editor.actRules.value = v
  },
  get timeRange() {
    return timeRange.value
  },
  set timeRange(v: string[]) {
    timeRange.value = v || []
  }
})

const basicRules: FormRules = {
  actName: [{ required: true, message: '请填写活动名称', trigger: 'blur' }],
  actRules: [{ required: true, message: '请填写活动规则', trigger: 'blur' }],
  timeRange: [
    {
      required: true,
      trigger: 'change',
      validator: (_rule, _value, callback) => {
        if (!editor.startTime.value || !editor.endTime.value) {
          callback(new Error('请选择活动时间'))
          return
        }
        if (new Date(editor.endTime.value) <= new Date(editor.startTime.value)) {
          callback(new Error('结束时间必须晚于开始时间'))
          return
        }
        callback()
      }
    }
  ]
}

watch(
  () => [editor.startTime.value, editor.endTime.value] as const,
  ([start, end]) => {
    if (start && end && (timeRange.value[0] !== start || timeRange.value[1] !== end)) {
      timeRange.value = [start, end]
    }
  },
  { immediate: true }
)

watch(timeRange, (v) => {
  editor.startTime.value = v?.[0] || ''
  editor.endTime.value = v?.[1] || ''
})

const bgIds = computed(() =>
  (festivalTabs.value[0]?.templates || []).slice(0, 4).map((t) => t.id!).filter(Boolean)
)
const bannerUrl = computed(() => {
  if (editor.selectedBgId.value && getCoverUrl(editor.selectedBgId.value)) {
    return getCoverUrl(editor.selectedBgId.value)
  }
  if (editor.initData.value?.coverUrl) return editor.initData.value.coverUrl
  if (bgIds.value[0]) return getCoverUrl(bgIds.value[0])
  return undefined
})

watch(
  () => [props.templateId, props.activityId, props.mode] as const,
  async ([templateId, activityId, mode]) => {
    edStep.value = mode === 'preview' ? 2 : 0
    if (activityId) {
      try {
        const loadMode = mode === 'create' ? 'edit' : mode === 'detail' || mode === 'preview' ? mode : 'edit'
        await editor.loadActivity(activityId, loadMode)
      } catch {
        handleBack()
      }
      return
    }
    if (!templateId) return
    try {
      await editor.loadEditor(templateId)
      if (!editor.selectedBgId.value) {
        editor.selectedBgId.value = templateId
      }
    } catch {
      handleBack()
    }
  },
  { immediate: true }
)

const stepClass = (i: number) => ({
  'is-active': i === edStep.value,
  'is-done': i < edStep.value,
  'is-clickable': readonly.value || standalone.value || i <= edStep.value
})

const goToStep = async (target: number) => {
  if (target === edStep.value) return
  if (readonly.value || standalone.value) {
    edStep.value = target
    return
  }
  if (target < edStep.value) {
    edStep.value = target
    return
  }
  if (target === edStep.value + 1) {
    await nextStep()
  }
}

const addPrize = () => {
  editor.prizes.value.push({ name: `奖品${editor.prizes.value.length + 1}`, type: '实物', count: 1, probability: 0 })
}

const removePrize = (i: number) => {
  editor.prizes.value.splice(i, 1)
}

const nextStep = async () => {
  if (readonly.value) {
    if (edStep.value < 3) edStep.value++
    return
  }
  if (edStep.value === 0) {
    const valid = await basicFormRef.value?.validate().catch(() => false)
    if (!valid) return
  }
  if (edStep.value === 2) {
    const id = await editor.saveDraft()
    if (!id) return
  }
  if (edStep.value < 3) edStep.value++
}

const handlePublish = async () => {
  const ok = await editor.publish()
  if (ok) edStep.value = 3
}

const switchToEdit = () => {
  editor.editorMode.value = 'edit'
}

const goActivityList = () => goList()
const goActivityEdit = () => {
  if (props.standalone && editor.activityId.value) {
    edStep.value = 0
    editor.editorMode.value = 'edit'
    return
  }
  if (editor.activityId.value) {
    goList()
  }
}
</script>

<style lang="scss" scoped>
/** 活动市场：全屏门户向导（负边距贴齐活动市场页） */
.mp-editor {
  box-sizing: border-box;
  margin: -20px -24px;
  min-height: calc(100% + 40px);
  background: #f5f6fa;
  padding-bottom: 24px;

  &__top {
    position: sticky;
    top: 0;
    z-index: 100;
    height: 52px;
    background: #fff;
    box-shadow: 0 1px 4px rgb(0 0 0 / 6%);
    display: flex;
    align-items: center;
    padding: 0 20px;
    gap: 20px;
  }

  &__back {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #606266;
    cursor: pointer;
    font-size: 14px;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
  }

  &__steps {
    display: flex;
    align-items: center;
    margin: 0 auto;
  }

  &__step {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: default;

    &.is-clickable {
      cursor: pointer;
    }

    &.is-active .mp-editor__num {
      background: #2b6de8;
      color: #fff;
    }

    &.is-active .mp-editor__txt {
      color: #2b6de8;
      font-weight: 600;
    }

    &.is-done .mp-editor__num {
      background: #67c23a;
      color: #fff;
    }
  }

  &__num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #e8eaf0;
    color: #909399;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__txt {
    font-size: 13px;
    color: #909399;
  }

  &__line {
    width: 60px;
    height: 1px;
    background: #e0e3ea;
    margin: 0 10px;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  &__body {
    display: flex;
    gap: 18px;
    padding: 18px 24px 90px;
    max-width: 1400px;
    margin: 0 auto;
  }

  &__left {
    flex: 1;
    min-width: 0;
  }

  &__right {
    width: 380px;
    flex-shrink: 0;
    position: sticky;
    top: 70px;
    align-self: flex-start;
  }

  &__preview-label {
    text-align: center;
    font-size: 12px;
    color: #909399;
    margin-bottom: 10px;
    letter-spacing: 0.5px;
  }
}

/** 活动列表 → 修改：嵌入 ContentWrap，与详情/预览一致 */
.mp-editor-embedded {
  box-sizing: border-box;
  width: 100%;

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding-bottom: 16px;
    margin-bottom: 4px;
    border-bottom: 1px solid var(--el-border-color-lighter);
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #303133;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
  }

  &__steps {
    margin: 16px 0;
  }

  &__body {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  &__left {
    flex: 1;
    min-width: 0;
  }

  &__right {
    width: 340px;
    flex-shrink: 0;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid var(--el-border-color-lighter);
  }

  .mp-ed-panel {
    margin-bottom: 12px;
  }
}

.mp-ed-panel {
  background: #fff;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 14px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 16px;
    padding-left: 9px;
    border-left: 3px solid #2b6de8;
  }
}

.mp-frow {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  &--nested {
    margin-bottom: 10px;
  }
}

.mp-flabel {
  width: 96px;
  flex-shrink: 0;
  font-size: 13px;
  color: #606266;
  padding-top: 7px;
  text-align: right;
  padding-right: 12px;

  &--sm {
    width: 70px;
    text-align: left;
    padding-right: 0;
  }

  .req {
    color: #f56c6c;
    margin-right: 2px;
  }
}

.mp-fctl {
  flex: 1;
}

.mp-finput {
  width: 100%;
  height: 34px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 13px;

  &--sm {
    width: 70px;
  }

  &:focus {
    border-color: #2b6de8;
    outline: none;
  }
}

.mp-ftextarea {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  min-height: 72px;
  resize: vertical;
}

.mp-preview-tip {
  background: #f0f6ff;
  border: 1px solid #d9e8ff;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-size: 12px;
  line-height: 1.6;
  color: #606266;

  p {
    margin: 0;

    & + p {
      margin-top: 6px;
    }
  }

  strong {
    color: #2b6de8;
    font-weight: 600;
  }
}

.mp-fhint {
  font-size: 12px;
  color: #b0b3ba;
  margin-top: 5px;
}

.mp-daterange {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mp-switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #606266;

  &--inline {
    margin-bottom: 10px;
  }
}

.mp-prize-el-table {
  width: 100%;
  margin-bottom: 12px;
}

.mp-btn-add-el {
  width: 100%;
}

.mp-swatches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.mp-swatch {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid transparent;

  &.is-active {
    border-color: #303133;
  }
}

.mp-bg-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.mp-bg-item {
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;

  img {
    width: 100%;
    aspect-ratio: 10 / 7;
    object-fit: cover;
    display: block;
  }

  &.is-active {
    border-color: #2b6de8;
  }
}

.mp-bg-ck {
  position: absolute;
  right: 4px;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2b6de8;
  color: #fff;
  font-size: 10px;
  display: none;
  align-items: center;
  justify-content: center;
}

.mp-bg-item.is-active .mp-bg-ck {
  display: flex;
}

.mp-finish {
  text-align: center;
  padding: 30px 0 10px;

  &__ok {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #67c23a;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
  }

  h2 {
    font-size: 18px;
    margin-bottom: 8px;
  }

  p {
    color: #909399;
    font-size: 13px;
    margin-bottom: 18px;
  }

  &__link {
    background: #f5f7fa;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    color: #606266;
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
    margin-bottom: 16px;

    span {
      word-break: break-all;
      text-align: left;
    }

    button,
    .mp-finish__copy {
      color: #2b6de8;
      font-size: 13px;
      border: none;
      background: none;
      cursor: pointer;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
    }
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 12px;
  }
}

.mp-step-btns {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.mp-btn-ghost {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 6px 16px;
  font-size: 13px;
  color: #606266;
  background: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.mp-btn-primary {
  background: #2b6de8;
  color: #fff;
  border-radius: 4px;
  padding: 6px 18px;
  font-size: 13px;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.mp-btn-success {
  background: #67c23a;
  color: #fff;
  border-radius: 4px;
  padding: 6px 18px;
  font-size: 13px;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
